// src/redux/thunks/thunk-recipes.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { Recipe, MealPlans } from "../../models/types";
import {
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

// must match the pool config used elsewhere (partners-logged-in, etc)
const poolData = {
  UserPoolId: "us-east-2_nECr2VdPc",
  ClientId: "6jd4v12t5keoj2eupcrjkcia2c",
};
const userPool = new CognitoUserPool(poolData);

export const API_URL_recipe =
  "https://m01op8z79h.execute-api.us-east-2.amazonaws.com/default/avro-recipes";

/**
 * Grab a fresh/valid Cognito ID token before every API call.
 * Returns null if we can't.
 */
export async function getFreshIdToken(): Promise<string | null> {
  return new Promise((resolve) => {
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      resolve(null);
      return;
    }
    currentUser.getSession((err: any, session: CognitoUserSession | null) => {
      if (err) {
        console.error("Error retrieving session:", err);
        resolve(null);
        return;
      }
      if (session && session.isValid()) {
        resolve(session.getIdToken().getJwtToken());
      } else {
        resolve(null);
      }
    });
  });
}

/**
 * GET /avro-recipes?userId=...
 * Lambda returns:
 * {
 *   mealPlans: [...],
 *   recipes: [...]
 * }
 *
 * We'll normalize this into:
 * {
 *   recipes: Recipe[];
 *   mealPlans: MealPlans[];
 * }
 *
 * We infer userId (trainer) from Redux partners slice.
 */
export const fetchRecipesAndMealPlans = createAsyncThunk<
  { recipes: Recipe[]; mealPlans: MealPlans[] },
  void,
  { state: RootState; rejectValue: string }
>("recipes/fetchRecipesAndMealPlans", async (_, thunkAPI) => {
  const state = thunkAPI.getState();
  const userId = state.partners.partnerData?.partnerData?.userId || "";

  if (!userId) {
    return thunkAPI.rejectWithValue("No trainer userId in Redux.");
  }

  // Lambda example doesn't explicitly require Authorization for GET,
  // but our other calls send Authorization, so we'll keep it consistent.
  const idToken = await getFreshIdToken();
  if (!idToken) {
    return thunkAPI.rejectWithValue("Missing/invalid Cognito token.");
  }

  const url = `${API_URL_recipe}?userId=${encodeURIComponent(userId)}`;

  try {
    const res = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: idToken,
      },
    });

    if (!res.ok) {
      const txt = await res.text();
      console.error("Failed to fetch recipes+mealPlans", txt);
      return thunkAPI.rejectWithValue(`Fetch failed with status ${res.status}`);
    }

    const data = await res.json();

    const recipes = Array.isArray(data.recipes) ? data.recipes : [];
    const mealPlans = Array.isArray(data.mealPlans) ? data.mealPlans : [];

    return { recipes, mealPlans };
  } catch (err: any) {
    console.error("Network error fetching recipes+mealPlans", err);
    return thunkAPI.rejectWithValue(
      "Network error fetching recipes and meal plans."
    );
  }
});

/**
 * Upsert (create/update) a single recipe.
 *
 * Lambda expects POST body:
 * {
 *   userId,
 *   recipes: [
 *     {
 *       id: recipe.recipeId,
 *       ...recipe
 *     }
 *   ]
 * }
 *
 * Lambda responds { ok: true, message: "Recipes upserted" }
 *
 * We return { ok, recipe } so the slice can immediately update state.recipes.
 */
export const upsertRecipe = createAsyncThunk<
  { ok: boolean; recipe: Recipe },
  { recipe: Recipe },
  { state: RootState; rejectValue: string }
>("recipes/upsertRecipe", async ({ recipe }, thunkAPI) => {
  const state = thunkAPI.getState();

  const loggedInUserId =
    state.partners.partnerData?.partnerData?.userId || recipe.userId || "";

  if (!loggedInUserId) {
    return thunkAPI.rejectWithValue("No trainer userId in Redux.");
  }

  const idToken = await getFreshIdToken();
  if (!idToken) {
    return thunkAPI.rejectWithValue("Missing/invalid Cognito token.");
  }

  const payload = {
    userId: loggedInUserId,
    recipes: [
      {
        // Lambda wants `id` which becomes recipeId in Dynamo.
        id: recipe.recipeId,
        ...recipe,
        userId: loggedInUserId,
        createdByTrainerId:
          recipe.createdByTrainerId || loggedInUserId || recipe.userId || "",
      },
    ],
  };

  try {
    const res = await fetch(API_URL_recipe, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: idToken,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const text = await res.text();
      console.error("Failed to upsert recipe", text);
      return thunkAPI.rejectWithValue(
        `Recipe upsert failed with status ${res.status}`
      );
    }

    // We already know what we sent, so we can derive final recipe shape.
    const finalRecipe: Recipe = {
      ...recipe,
      userId: loggedInUserId,
      createdByTrainerId:
        recipe.createdByTrainerId || loggedInUserId || recipe.userId || "",
    };

    return { ok: true, recipe: finalRecipe };
  } catch (err: any) {
    console.error("Network error upserting recipe", err);
    return thunkAPI.rejectWithValue("Network error upserting recipe.");
  }
});

/**
 * DELETE /avro-recipes?userId=...&recipeId=...
 *
 * Lambda handles:
 *   deleteRecipe(qs.userId, qs.recipeId)
 *   -> dynamo.delete({ TableName: RECIPES_TABLE, Key: { userId, recipeId } })
 *
 * We just return { ok, recipeId } so the slice can prune state.recipes.
 */
export const deleteRecipeThunk = createAsyncThunk<
  { ok: boolean; recipeId: string },
  { recipeId: string },
  { state: RootState; rejectValue: string }
>("recipes/deleteRecipe", async ({ recipeId }, thunkAPI) => {
  const state = thunkAPI.getState();

  const loggedInUserId = state.partners.partnerData?.partnerData?.userId || "";

  if (!loggedInUserId) {
    return thunkAPI.rejectWithValue("No trainer userId in Redux.");
  }

  const idToken = await getFreshIdToken();
  if (!idToken) {
    return thunkAPI.rejectWithValue("Missing/invalid Cognito token.");
  }

  const url = `${API_URL_recipe}?userId=${encodeURIComponent(
    loggedInUserId
  )}&recipeId=${encodeURIComponent(recipeId)}`;

  try {
    const res = await fetch(url, {
      method: "DELETE",
      headers: {
        Authorization: idToken,
      },
    });

    if (!res.ok) {
      const text = await res.text();
      console.error("Failed to delete recipe", text);
      return thunkAPI.rejectWithValue(
        `Recipe delete failed with status ${res.status}`
      );
    }

    return { ok: true, recipeId };
  } catch (err: any) {
    console.error("Network error deleting recipe", err);
    return thunkAPI.rejectWithValue("Network error deleting recipe.");
  }
});

// src/redux/thunks/thunk-recipes.ts
export const upsertRecipesBulk = createAsyncThunk<
  { ok: boolean; count: number },
  { recipes: Recipe[]; targetUserId: string },
  { state: RootState; rejectValue: string }
>("recipes/upsertRecipesBulk", async ({ recipes, targetUserId }, thunkAPI) => {
  if (!targetUserId) return thunkAPI.rejectWithValue("Missing targetUserId");

  const idToken = await getFreshIdToken();
  if (!idToken)
    return thunkAPI.rejectWithValue("Missing/invalid Cognito token.");

  const payload = {
    userId: targetUserId,
    recipes: recipes.map((r) => ({
      id: r.recipeId, // becomes SK
      ...r,
      userId: targetUserId, // write under client PK
      assignedAt: new Date().toISOString(),
    })),
  };

  const res = await fetch(API_URL_recipe, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: idToken },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text();
    return thunkAPI.rejectWithValue(`Bulk upsert failed: ${text}`);
  }
  return { ok: true, count: recipes.length };
});
